const AUDIENCE_TYPE = {
    AGENT: 'Agent',
    CUSTOMER: 'Customer'
}

const fromBackend = (data) => {
    if (!data.DocumentTags) { 
        data.DocumentTags = []
        return data
    }

    if (typeof data.DocumentTags === 'string') {
        try {
            data.DocumentTags = JSON.parse(data.DocumentTags)
        } catch (e) {
            data.DocumentTags = [data.DocumentTags]
        }
    }

    if (!Array.isArray(data.DocumentTags)) {
        data.DocumentTags = []
    }

    const possibleAudience = [
        `q:audience:${AUDIENCE_TYPE.AGENT}`,
        `q:audience:${AUDIENCE_TYPE.CUSTOMER}`,
        // There was a bug on frontend, so it might be without q: prefix
        `qaudience${AUDIENCE_TYPE.AGENT}`,
        `qaudience${AUDIENCE_TYPE.CUSTOMER}`,
    ]

    data.Audience = AUDIENCE_TYPE.AGENT

    possibleAudience.forEach((audience) => {
        if (data.DocumentTags.includes(audience)) {
            data.DocumentTags = data.DocumentTags.filter((tag) => tag.trim() !== audience)
            data.Audience = audience.replace('q:audience:', '').replace('qaudience', '')
        }
    })

    // We have a bad data in the backend, because frontend wasn't trimming tags before
    // Now we have to trim it here to avoid inconsistency
    data.DocumentTags = data.DocumentTags.map((tag) => tag.trim().replace(/[^a-zA-Z 0-9]+/gm, '')).filter((tag) => tag !== '')

    if (typeof data.TenantPermissionIDs === 'string') {
        data.TenantPermissionIDs = JSON.parse(data.TenantPermissionIDs)
    }

    return data
}

const toBackend = (data) => {
    if (!data.DocumentTags) { data.DocumentTags = [] }

    if (data.Audience == AUDIENCE_TYPE.AGENT) {
        data.DocumentTags.push(`q:audience:${AUDIENCE_TYPE.AGENT}`)
    } else if (data.Audience == AUDIENCE_TYPE.CUSTOMER) {
        data.DocumentTags.push(`q:audience:${AUDIENCE_TYPE.CUSTOMER}`)
    }

    data.DocumentTags = JSON.stringify(data.DocumentTags.map((tag) => tag.trim()).filter((tag) => tag !== ''))
    data.TenantPermissionIDs = JSON.stringify(data.TenantPermissionIDs || [])

    return data
}

export default {
    getTenantDocumentList(TenantID, category, options) {
        if (typeof TenantID == 'undefined' || TenantID == null || TenantID == '') {
            return new Promise((resolve, reject) => { reject() });
        }
        var url = '/api/private/agent_document';
        this.cancelQRequest("divisionDocumentsQ")

        if (category === '-All-') {
            category = null;
        }

        const documents = this.getRequest(url, { 'TenantID': TenantID, 'CategoryName': category, ...options }, 'divisionDocumentsQ').then(function(json) {
            return (json).data.map(fromBackend);
        })

        const marketingResources = this.getMarketingResources(category)

        return Promise.all([documents, marketingResources])
            .then(([documents, marketingResources]) => {
                return [...documents, ...marketingResources]
                    .sort((a, b) => {
                        if (a.LastChangeDate !== b.LastChangeDate) {
                            // Show newest first
                            return b.LastChangeDate.localeCompare(a.LastChangeDate)
                        }

                        return a.DocTitle.localeCompare(b.DocTitle)
                    })
            })
    },

    deleteTenantDocument(id) {
        return this.deleteRequest('/api/private/agent_document' + id).then(
            function(json) {
                return json
            },
            function(err) {
                if (err.status == 404) {
                    var msg = "Document not found"
                } else {
                    msg = err.response.message
                }
                return (msg)
            })
    },

    async uploadTenantDocument(doc, file, id = doc.ID) {
        var data = toBackend({
            DocTitle: doc.DocTitle,
            DocDesc: doc.DocDesc,
            TenantID: doc.TenantID,
            CategoryName: doc.CategoryName,
            SecureDocumentInd: doc.SecureDocumentInd,
            document: file,
            ...doc,
        })

        const formData = new FormData();

        for (const name in data) {
            if (data.hasOwnProperty(name) && data[name] !== undefined) {
                if (Array.isArray(data[name])) {
                    data[name].forEach((value) => formData.append(name, value));
                    continue;
                }
                formData.append(name, typeof data[name] === 'string' || data[name] instanceof File ? data[name] : data[name]);
            }
        }

        let url = '/api/private/agent_document';

        if (id) {
            url = url + '/' + id;
        }

        const token = await this.config()

        return fetch(url, {
            method: 'POST',
            body: formData,
            headers: {
                'Authorization': `Bearer ${token}`
            }
        })
        .then((r) => r.json())
        .then(({ file, ...rest }) => ({
            file: fromBackend(file),
            ...rest,
        }))
    },

    updateTenantDocument(doc) {
        var d = {
            DocTitle: doc.DocTitle,
            DocDesc: doc.DocDesc,
            TenantID: doc.TenantID,
            CategoryName: doc.CategoryName,
            SecureDocumentInd: doc.SecureDocumentInd
        }
        return this.putRequest('/api/private/agent_document/' + doc.ID, d)
            .then(({ file, ...rest }) => ({
                file: fromBackend(file),
                ...rest,
            }))
    },

    async getMarketingResources(group, tags = []) {
        const url = '/api/my/library-documents';

        const items = (await this.getRequest(url, { 'SubCategory': group, CategoryName: 'Marketing Resource Library' }))
            .data
            .map(fromBackend)

        if (tags.length == 0) {
            return items
        }

        return items
            .filter((item) => item.DocumentTags.some((tag) => tags.includes(tag)))
            .sort((a, b) => {
                if (a.LastChangeDate !== b.LastChangeDate) {
                    // Show newest first
                    return b.LastChangeDate.localeCompare(a.LastChangeDate)
                }

                return a.DocTitle.localeCompare(b.DocTitle)
            })
    },

    // TODO: Would be nice to have separated requests
    /**
     * @param {Array<string>} groups 
     * @returns {Promise<string[]>}
     */
    async getMarketingResourcesTagsList(groups = []) {
        let items = (await this.getMarketingResources())

        if (groups.length) {
            items = items.filter((item) => groups.includes(item.SubCategory))
        }

        return items
            .map((item) => item.DocumentTags)
            .flat()
            .filter((item, index, self) => self.indexOf(item) === index)
            .filter((item) => item !== null && item !== undefined && item !== '')
    },

    async getMarketingResourcesGroupsList() {
        return (await this.getMarketingResources())
            .map((item) => item.SubCategory)
            .filter((item, index, self) => self.indexOf(item) === index)
            .filter((item) => item !== null && item !== undefined && item !== '')
    }
}